import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Edit,
  DollarSign,
  Building,
  Target,
  Settings,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

interface VaPlatformPricing {
  id: number;
  revenue_band: string;
  annual_price_usd: string;
}

interface VaPillarPricing {
  id: number;
  revenue_band: string;
  pillar_name: string;
  annual_price_usd: string;
}

interface VaEntityPricingTier {
  id: number;
  min_entities: number;
  max_entities: number;
  price_modifier: string;
  effective_units: string;
}

export default function AdminVaPricingScreen() {
  const [isEditPlatformOpen, setIsEditPlatformOpen] = useState(false);
  const [isEditPillarOpen, setIsEditPillarOpen] = useState(false);
  const [isEditEntityOpen, setIsEditEntityOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<VaPlatformPricing | null>(null);
  const [selectedPillar, setSelectedPillar] = useState<VaPillarPricing | null>(null);
  const [selectedEntity, setSelectedEntity] = useState<VaEntityPricingTier | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch VA platform pricing
  const { data: platformPricing = [], isLoading: platformLoading } = useQuery({
    queryKey: ['/api/admin/va-platform-pricing'],
    queryFn: () => apiRequest('/api/admin/va-platform-pricing')
  });

  // Fetch VA pillar pricing
  const { data: pillarPricing = [], isLoading: pillarLoading } = useQuery({
    queryKey: ['/api/admin/va-pillar-pricing'],
    queryFn: () => apiRequest('/api/admin/va-pillar-pricing')
  });

  // Fetch VA entity pricing tiers
  const { data: entityPricing = [], isLoading: entityLoading } = useQuery({
    queryKey: ['/api/admin/va-entity-pricing-tiers'],
    queryFn: () => apiRequest('/api/admin/va-entity-pricing-tiers')
  });

  // Update platform pricing mutation
  const updatePlatformMutation = useMutation({
    mutationFn: ({ id, ...data }: VaPlatformPricing) =>
      apiRequest(`/api/admin/va-platform-pricing/${id}`, {
        method: 'PUT',
        body: data
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/va-platform-pricing'] });
      setIsEditPlatformOpen(false);
      setSelectedPlatform(null);
      toast({
        title: "Success",
        description: "Platform pricing updated successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update platform pricing",
        variant: "destructive"
      });
    }
  });

  // Update pillar pricing mutation
  const updatePillarMutation = useMutation({
    mutationFn: ({ id, ...data }: VaPillarPricing) =>
      apiRequest(`/api/admin/va-pillar-pricing/${id}`, {
        method: 'PUT',
        body: data
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/va-pillar-pricing'] });
      setIsEditPillarOpen(false);
      setSelectedPillar(null);
      toast({
        title: "Success",
        description: "Pillar pricing updated successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update pillar pricing",
        variant: "destructive"
      });
    }
  });

  // Update entity pricing mutation
  const updateEntityMutation = useMutation({
    mutationFn: ({ id, ...data }: VaEntityPricingTier) =>
      apiRequest(`/api/admin/va-entity-pricing-tiers/${id}`, {
        method: 'PUT',
        body: data
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/va-entity-pricing-tiers'] });
      setIsEditEntityOpen(false);
      setSelectedEntity(null);
      toast({
        title: "Success",
        description: "Entity pricing updated successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update entity pricing",
        variant: "destructive"
      });
    }
  });

  const handleUpdatePlatform = (formData: FormData) => {
    if (!selectedPlatform) return;
    
    const data = {
      ...selectedPlatform,
      annual_price_usd: formData.get('annual_price_usd') as string
    };
    updatePlatformMutation.mutate(data);
  };

  const handleUpdatePillar = (formData: FormData) => {
    if (!selectedPillar) return;
    
    const data = {
      ...selectedPillar,
      annual_price_usd: formData.get('annual_price_usd') as string
    };
    updatePillarMutation.mutate(data);
  };

  const handleUpdateEntity = (formData: FormData) => {
    if (!selectedEntity) return;
    
    const data = {
      ...selectedEntity,
      min_entities: parseInt(formData.get('min_entities') as string),
      max_entities: parseInt(formData.get('max_entities') as string),
      price_modifier: formData.get('price_modifier') as string,
      effective_units: formData.get('effective_units') as string
    };
    updateEntityMutation.mutate(data);
  };

  const revenueBands = ['emerging', 'enterprise', 'large'];
  const pillars = ['account_reconciliation', 'journal_entry', 'intercompany', 'close_management'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">VA Pricing Management</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage Value Assessment pricing structure (Current System)
          </p>
        </div>
        <div className="flex items-center space-x-2 text-green-600">
          <Target className="w-5 h-5" />
          <span className="font-medium">Active Pricing System</span>
        </div>
      </div>

      {/* Warning about legacy tables */}
      <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20">
        <CardContent className="pt-6">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            <div>
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Legacy pricing tables have been removed
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                Only VA pricing tables are now used for calculations. Legacy Value Engineering pricing has been backed up and removed.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="platform" className="space-y-4">
        <TabsList>
          <TabsTrigger value="platform">Platform Pricing</TabsTrigger>
          <TabsTrigger value="pillars">Pillar Pricing</TabsTrigger>
          <TabsTrigger value="entities">Entity Tiers</TabsTrigger>
        </TabsList>

        <TabsContent value="platform">
          <Card>
            <CardHeader>
              <CardTitle>Platform Base Pricing</CardTitle>
              <CardDescription>
                Base platform pricing by revenue band
              </CardDescription>
            </CardHeader>
            <CardContent>
              {platformLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span className="ml-2">Loading platform pricing...</span>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Revenue Band</TableHead>
                      <TableHead>Annual Price (USD)</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {platformPricing.map((pricing: VaPlatformPricing) => (
                      <TableRow key={pricing.id}>
                        <TableCell className="font-medium capitalize">{pricing.revenue_band}</TableCell>
                        <TableCell>${parseInt(pricing.annual_price_usd).toLocaleString()}</TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedPlatform(pricing);
                              setIsEditPlatformOpen(true);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pillars">
          <Card>
            <CardHeader>
              <CardTitle>Pillar Pricing</CardTitle>
              <CardDescription>
                Per-pillar pricing by revenue band
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pillarLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span className="ml-2">Loading pillar pricing...</span>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Revenue Band</TableHead>
                      <TableHead>Pillar</TableHead>
                      <TableHead>Annual Price (USD)</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pillarPricing.map((pricing: VaPillarPricing) => (
                      <TableRow key={pricing.id}>
                        <TableCell className="capitalize">{pricing.revenue_band}</TableCell>
                        <TableCell className="capitalize">{pricing.pillar_name.replace('_', ' ')}</TableCell>
                        <TableCell>${parseInt(pricing.annual_price_usd).toLocaleString()}</TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedPillar(pricing);
                              setIsEditPillarOpen(true);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="entities">
          <Card>
            <CardHeader>
              <CardTitle>Entity Pricing Tiers</CardTitle>
              <CardDescription>
                Entity-based pricing modifiers and thresholds
              </CardDescription>
            </CardHeader>
            <CardContent>
              {entityLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span className="ml-2">Loading entity pricing...</span>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Min Entities</TableHead>
                      <TableHead>Max Entities</TableHead>
                      <TableHead>Price Modifier</TableHead>
                      <TableHead>Effective Units</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entityPricing.map((tier: VaEntityPricingTier) => (
                      <TableRow key={tier.id}>
                        <TableCell>{tier.min_entities}</TableCell>
                        <TableCell>{tier.max_entities}</TableCell>
                        <TableCell>{parseFloat(tier.price_modifier).toFixed(2)}x</TableCell>
                        <TableCell>{tier.effective_units}</TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedEntity(tier);
                              setIsEditEntityOpen(true);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Platform Pricing Dialog */}
      <Dialog open={isEditPlatformOpen} onOpenChange={setIsEditPlatformOpen}>
        <DialogContent>
          {selectedPlatform && (
            <form onSubmit={(e) => {
              e.preventDefault();
              handleUpdatePlatform(new FormData(e.target as HTMLFormElement));
            }}>
              <DialogHeader>
                <DialogTitle>Edit Platform Pricing</DialogTitle>
                <DialogDescription>
                  Update pricing for {selectedPlatform.revenue_band} revenue band
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="annual_price_usd">Annual Price (USD)</Label>
                  <Input
                    id="annual_price_usd"
                    name="annual_price_usd"
                    type="number"
                    defaultValue={selectedPlatform.annual_price_usd}
                    required
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button type="submit" disabled={updatePlatformMutation.isPending}>
                  {updatePlatformMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Update Pricing
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Pillar Pricing Dialog */}
      <Dialog open={isEditPillarOpen} onOpenChange={setIsEditPillarOpen}>
        <DialogContent>
          {selectedPillar && (
            <form onSubmit={(e) => {
              e.preventDefault();
              handleUpdatePillar(new FormData(e.target as HTMLFormElement));
            }}>
              <DialogHeader>
                <DialogTitle>Edit Pillar Pricing</DialogTitle>
                <DialogDescription>
                  Update pricing for {selectedPillar.pillar_name} in {selectedPillar.revenue_band} band
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="annual_price_usd">Annual Price (USD)</Label>
                  <Input
                    id="annual_price_usd"
                    name="annual_price_usd"
                    type="number"
                    defaultValue={selectedPillar.annual_price_usd}
                    required
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button type="submit" disabled={updatePillarMutation.isPending}>
                  {updatePillarMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Update Pricing
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Entity Pricing Dialog */}
      <Dialog open={isEditEntityOpen} onOpenChange={setIsEditEntityOpen}>
        <DialogContent>
          {selectedEntity && (
            <form onSubmit={(e) => {
              e.preventDefault();
              handleUpdateEntity(new FormData(e.target as HTMLFormElement));
            }}>
              <DialogHeader>
                <DialogTitle>Edit Entity Pricing Tier</DialogTitle>
                <DialogDescription>
                  Update entity-based pricing tier configuration
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="min_entities">Min Entities</Label>
                    <Input
                      id="min_entities"
                      name="min_entities"
                      type="number"
                      defaultValue={selectedEntity.min_entities}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="max_entities">Max Entities</Label>
                    <Input
                      id="max_entities"
                      name="max_entities"
                      type="number"
                      defaultValue={selectedEntity.max_entities}
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="price_modifier">Price Modifier</Label>
                    <Input
                      id="price_modifier"
                      name="price_modifier"
                      type="number"
                      step="0.01"
                      defaultValue={selectedEntity.price_modifier}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="effective_units">Effective Units</Label>
                    <Input
                      id="effective_units"
                      name="effective_units"
                      type="number"
                      step="0.01"
                      defaultValue={selectedEntity.effective_units}
                      required
                    />
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button type="submit" disabled={updateEntityMutation.isPending}>
                  {updateEntityMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Update Tier
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}